import React from 'react';
import classNames from 'classnames';
import config from 'config/config';
import SocialButton from 'src/components/buttons/socialButton';
import { setLangCookie } from 'src/utils/cookie';
import i18n from 'src/utils/translate';
import ButtonDropdown from 'src/components/buttons/buttonDropdown/buttonDropdown';
import { DropdownItem } from 'reactstrap';
import LazyImage from 'src/utils/lazyImage';
import { useHelpScout } from 'src/utils/hooks';
import styled from 'styled-components';
import { textLinkWhite } from 'styles-js/mixins/typography';

const FooterLinkButton = styled.button`
  ${textLinkWhite};
  border: none;
  background: none;
  font-weight: 600;
  margin-right: 20px;

  @media (${props => props.theme.overScreenSmall}) {
    font-size: 18px;
    margin-right: 50px;
  }
`;

const year = new Date().getFullYear();

const LANGUAGES = {
  'en': 'English',
  'es': 'Español',
  'pt': 'Português',
};

class Footer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isReduced: true,
      isCompanySectionHidden: true,
      isProductSectionHidden: true,
      isSupportSectionHidden: true,
    };
  }

  onLanguageSelected = (lang) => {
    setLangCookie(lang);
    this.props.updateLanguage(lang);
  }

  renderBlock = ({ title, isSectionHiddenKey, links }) => {
    const isSectionHidden = this.state[isSectionHiddenKey];
    const iconClassName = classNames({
      'icon': true,
      'visible-xs': true,
      'icon-down-open': isSectionHidden,
      'icon-up-open': !isSectionHidden,
    });
    const linksClassName = classNames({
      'hidden-xs': isSectionHidden,
    });

    return (
      <div className="footer-col-2 footer__block" key={`footer-section-${title}`}>
        <div className="footer__block-header">
          <h2>{title}</h2>
          <span
            className={iconClassName}
            onClick={() => this.setState({ [isSectionHiddenKey]: !isSectionHidden })}
          />
        </div>
        <div className={linksClassName}>
          {links.map(link => (
            <p key={`link-${link.text}`}>
              <a
                href={link.href}
                rel="noopener"
                target="_blank"
                onClick={(e) => {
                  if (link.onClick) {
                    link.onClick(e);
                  }
                }}
              >
                {link.text}
              </a>
            </p>
          ))}
        </div>
      </div>
    );
  }

  renderAppStoreLogo() {
    return (
      <a href="https://itunes.apple.com/app/apple-store/id1016950959?pt=1924360&ct=footer&mt=8" rel="noopener"
        target="_blank">
        <LazyImage
          alt="App store"
          classnames="footer__appstore-image"
          src={`${config.cdn.static}img/home/app-store.png`}
        />
        <div className="footer__app-stars" >
          &#9733; &#9733; &#9733; &#9733; &#9733;  {i18n.t('Average rating')}: 4.8
        </div>
      </a>
    );
  }

  onHelpClick = (e) => {
    e.preventDefault();
    window.HelpscoutBeacon.triggerHelpScoutBeacon('USER_SUPPORT');
  }

  onGetHelpClick = (e) => {
    e.preventDefault();
    window.HelpscoutBeacon.triggerHelpScoutArticle('11-community-guidelines');
  }

  onCookiesSettingsClick = (e) => {
    e.preventDefault();
    if (window.CookieControl) {
      window.CookieControl.open();
    }
  }

  render() {
    const { isLoggedIn } = this.props;
    const lang = i18n.language || 'en';
    const footerClasses = classNames({
      'footer': true,
      'hidden-xs': isLoggedIn && this.state.isReduced,
    });

    const footerBottomClasses = classNames({
      'footer__bottom': true,
      'hidden-xs': isLoggedIn && this.state.isReduced,
    });

    const reducedFooter = isLoggedIn && this.state.isReduced
      ? <div className="reduced-footer">
        <p>&copy; {year} HealthUnlocked</p>
        <div
          className="reduced-footer__show-more"
          onClick={() => this.setState({ isReduced: !this.state.isReduced })}>
          {i18n.t('Show footer')}
        </div>
      </div>
      : null;

    const sections = [
      {
        title: i18n.t('Company'),
        isSectionHiddenKey: 'isCompanySectionHidden',
        links: [
          { href: 'https://about.healthunlocked.com', text: i18n.t('About Us') },
          { href: 'https://about.healthunlocked.com/our-team', text: i18n.t('Our Team') },
          { href: 'https://about.healthunlocked.com', text: i18n.t('Partner with us') },
          { href: 'https://about.healthunlocked.com/careers', text: i18n.t('Careers') },
          { href: 'https://blog.healthunlocked.com', text: i18n.t('Blog') },
        ],
      },
      {
        title: i18n.t('Product'),
        isSectionHiddenKey: 'isProductSectionHidden',
        links: [
          { href: '/tags', text: i18n.t('Health terms A to Z') },
          { href: '/communities', text: i18n.t('Communities A to Z') },
          { href: 'https://itunes.apple.com/app/apple-store/id1016950959?pt=1924360&ct=footer&mt=8', text: i18n.t('iPhone app') },
          { href: 'https://about.healthunlocked.com/start-a-community', text: i18n.t('Start a Community') },
        ],
      },
      {
        title: i18n.t('Support'),
        isSectionHiddenKey: 'isSupportSectionHidden',
        links: [
          { href: '#', onClick: this.onHelpClick, text: i18n.t('Help Centre') },
          { href: '#', onClick: this.onGetHelpClick, text: i18n.t('Community Guidelines') },
          { href: 'https://about.healthunlocked.com/contact-us', text: i18n.t('Contact us') },
        ],
      },
    ];
    const cookiesSettingsLink = { href: '#', onClick: this.onCookiesSettingsClick, text: i18n.t('Cookies settings') };
    sections[2].links.push(cookiesSettingsLink);

    return (
      <footer role="contentinfo">
        {reducedFooter}
        <div className={footerClasses}>
          <div className="footer-columns">
            <div className="footer-col-2">
              <LazyImage
                alt="HealthUnlocked"
                classnames="logo"
                src={`${config.cdn.static}img/healthunlocked-logo-white.png`}
              />
              <div className="footer-copy">&copy; {year} {i18n.t('All rights reserved.')}</div>
              <div className="footer__social">
                <div itemScope={true} itemType="https://schema.org/Organization">
                  <link href="https://healthunlocked.com" itemProp="url"/>
                  <a aria-label="Facebook" href="https://facebook.com/healthunlocked"
                    itemProp="sameAs" rel="noopener"
                    target="_blank" title="Facebook">
                    <SocialButton brand="facebook" />
                  </a>
                  <a aria-label="Twitter" href="https://twitter.com/healthunlocked"
                    itemProp="sameAs" rel="noopener"
                    target="_blank" title="Twitter">
                    <SocialButton brand="twitter" />
                  </a>
                  <a aria-label="YouTube" href="https://youtube.com/healthunlocked"
                    itemProp="sameAs" rel="noopener"
                    target="_blank" title="YouTube">
                    <SocialButton brand="youtube" />
                  </a>
                  <a aria-label="Instagram" href="https://instagram.com/healthunlocked"
                    itemProp="sameAs" rel="noopener"
                    target="_blank" title="Instagram">
                    <SocialButton brand="instagram" />
                  </a>
                </div>
              </div>
              <div className="footer__appstore-logo hidden-xs">
                {this.renderAppStoreLogo()}
              </div>
            </div>
            {sections.map(this.renderBlock)}
            <div className="footer__appstore-logo visible-xs">
              {this.renderAppStoreLogo()}
            </div>
            {
              isLoggedIn && !this.state.isReduced &&
              <div
                className="footer__hide visible-xs"
                onClick={() => this.setState({ isReduced: !this.state.isReduced })}>
                  {i18n.t('Hide footer')}
              </div>
            }
          </div>
        </div>
        <div className={footerBottomClasses}>
          <div className="footer__bottom-content">
            <div>
              <FooterLinkButton onClick={() => useHelpScout('147-terms', { type: 'modal' })}>{i18n.t('Terms')}</FooterLinkButton>
              <FooterLinkButton onClick={() => useHelpScout('148-privacy', { type: 'modal' })}>{i18n.t('Privacy')}</FooterLinkButton>
              <FooterLinkButton onClick={() => useHelpScout('151-security', { type: 'modal' })}>{i18n.t('Security')}</FooterLinkButton>
              <FooterLinkButton onClick={() => useHelpScout('150-cookies', { type: 'modal' })}>{i18n.t('Cookies')}</FooterLinkButton>
            </div>
            <div>
              <ButtonDropdown
                color="languages"
                text={`${i18n.t('Language')}: ${LANGUAGES[lang]}`}
              >
                {Object.keys(LANGUAGES).map(language => (
                  <DropdownItem
                    key={`lang-filter-${language}`}
                    onClick={() => this.onLanguageSelected(language)}
                  >
                    {LANGUAGES[language]}
                  </DropdownItem>
                ))}
              </ButtonDropdown>
            </div>
          </div>
        </div>
      </footer>
    );
  }
}

export default Footer;
